import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from 'zustand'
import { useTranslation } from 'react-i18next'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { RefreshCw, Save } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import { Badge } from '@quebon/ui/components/badge'
import { Alert, AlertDescription } from '@quebon/ui/components/alert'
import { cn } from '@quebon/ui/lib/utils'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@quebon/ui/components/dialog'
import { unitAssessmentAdminApi, type UaStatus } from '@/api/unitAssessmentAdmin'
import { useUnitAssessmentDetail } from '@/hooks/coaching/useUnitAssessments'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import { useAuthStore } from '@/store/authStore'
import { hasLcPermission } from '@/utils/roleUtils'
import { getApiErrorMessage } from '@/utils/apiError'
import { PartialCreditGradeToggle } from '../component-details/parts/PartialCreditGradeToggle'
import { GradingCorrectAnswer } from '../grading-sheet/GradingCorrectAnswer'
import { EMPTY_SCOPE, hasHandheldDrafts, type HandheldDraftStore } from './handheldDraftStore'

interface Props {
  presentation?: 'mobile' | 'workspace'
  readOnly?: boolean
  learnerId: number
  unitAssessmentId: number
  store: HandheldDraftStore
  onChanged: () => Promise<unknown>
}

const STATUS_LABEL: Record<UaStatus, string> = {
  PENDING: 'pending',
  STARTED: 'started',
  SUBMITTED: 'submitted',
  GRADED: 'graded',
  REVIEW_OPEN: 'reviewOpen',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
}

export function HandheldUnitAssessmentGrading({ presentation = 'mobile', readOnly = false, learnerId, unitAssessmentId, store, onChanged }: Props) {
  const { t } = useTranslation(['coaching', 'common'])
  const user = useAuthStore(s => s.user)
  const workspace = presentation === 'workspace'
  const canEdit = !readOnly && hasLcPermission(user ?? undefined, 'COACHING')
  const detail = useUnitAssessmentDetail(learnerId, unitAssessmentId)
  const qc = useQueryClient()
  const scope = `unit-assessment:${learnerId}:${unitAssessmentId}`
  const state = useStore(store, s => s.scopes[scope] ?? EMPTY_SCOPE)
  const saving = useStore(store, s => s.saving)
  const dirty = useStore(store, s => Object.values(s.scopes).some(value => Object.keys(value.drafts).length > 0))
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState(false)
  const data = detail.data
  const items = [...(data?.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
  const itemIds = new Set(items.map(item => item.quizId))
  const pending = Object.keys(state.drafts).length
  const unavailableDrafts = Object.keys(state.drafts).filter(id => !itemIds.has(id))
  const editableStatus = data != null && ['STARTED', 'SUBMITTED', 'GRADED', 'REVIEW_OPEN'].includes(data.status)
  const blocked = saving || !canEdit || !data || detail.isError
  const gradingBlocked = blocked || !editableStatus
  const allGraded = items.length > 0 && items.every(item => item.gradedIsCorrect != null)
  const canComplete = data?.status === 'GRADED' || data?.status === 'REVIEW_OPEN'
  const completeLabel = t(data?.status === 'GRADED' ? 'unitAssessment.openReview' : 'unitAssessment.complete')

  const refresh = async () => {
    const [result] = await Promise.all([
      detail.refetch({ throwOnError: true }),
      onChanged(),
      qc.invalidateQueries({ queryKey: coachingKeys.unitAssessments(learnerId) }),
      qc.invalidateQueries({ queryKey: ['coaching', 'learner', learnerId, 'today'] }),
      qc.invalidateQueries({ queryKey: coachingKeys.handheldGradingSummary() }),
    ])
    if (result.data) store.getState().clearAcknowledged(scope)
  }

  const save = async () => {
    if (gradingBlocked || !store.getState().beginSave()) return
    setError(null)
    try {
      const edits = Object.entries(store.getState().scopes[scope]?.drafts ?? {})
      for (const [quizId, grade] of edits) {
        if (!itemIds.has(quizId)) {
          store.getState().failed(scope, quizId, t('handheldGrading.staleTarget'))
          continue
        }
        try {
          // Omitting answer preserves the learner's submitted answer in both modes.
          const updated = await unitAssessmentAdminApi.gradePrintItem(learnerId, unitAssessmentId, quizId, undefined, grade === 1)
          qc.setQueryData(coachingKeys.unitAssessmentDetail(learnerId, unitAssessmentId), updated)
          store.getState().saved(scope, quizId, grade)
        } catch (e) {
          store.getState().failed(scope, quizId, getApiErrorMessage(e, t))
        }
      }
      await refresh()
    } catch (e) {
      setError(getApiErrorMessage(e, t))
    } finally {
      store.getState().endSave()
    }
  }

  const startPrint = async () => {
    if (blocked || data?.mode !== 'PRINT' || data.status !== 'PENDING' || hasHandheldDrafts(store) || !store.getState().beginSave()) return
    setError(null)
    try {
      const updated = await unitAssessmentAdminApi.startPrint(learnerId, unitAssessmentId)
      qc.setQueryData(coachingKeys.unitAssessmentDetail(learnerId, unitAssessmentId), { ...data, status: updated.status })
      await refresh()
    } catch (e) {
      setError(getApiErrorMessage(e, t))
    } finally {
      store.getState().endSave()
    }
  }

  const complete = async () => {
    if (blocked || !canComplete || !allGraded || hasHandheldDrafts(store) || !store.getState().beginSave()) return
    setError(null)
    try {
      // The API advances GRADED → REVIEW_OPEN, then REVIEW_OPEN → COMPLETED.
      const updated = await unitAssessmentAdminApi.complete(learnerId, unitAssessmentId)
      qc.setQueryData(coachingKeys.unitAssessmentDetail(learnerId, unitAssessmentId), updated)
      setConfirm(false)
      await refresh()
    } catch (e) {
      setError(getApiErrorMessage(e, t))
    } finally {
      store.getState().endSave()
    }
  }

  const retry = () => {
    setError(null)
    void refresh().catch(e => setError(getApiErrorMessage(e, t)))
  }

  if (detail.isError && !data) return <Alert variant="destructive"><AlertDescription>{t('unitAssessment.loadError')}<Button className="min-h-11" variant="outline" disabled={saving || detail.isFetching} onClick={() => void detail.refetch()}>{t('roster.retry')}</Button></AlertDescription></Alert>
  if (!data) return <p role="status">{t('unitAssessment.loading')}</p>

  return <section className={cn('flex min-w-0 flex-col gap-4', !workspace && pending > 0 && 'pb-24')} aria-label={data.setName || t('unitAssessment.title')} data-presentation={presentation}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-lg font-semibold">{workspace ? t('todayGrading.assessmentTitle') : <ContentMetadataText text={data.setName || t('unitAssessment.title')} />}</h2>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{t(`unitAssessment.mode.${data.mode}`)}</Badge>
        <Badge variant={data.status === 'COMPLETED' ? 'default' : 'outline'}>{t(`unitAssessment.statusLabel.${STATUS_LABEL[data.status]}`)}</Badge>
        <Button variant="outline" className="min-h-11" disabled={saving || dirty || detail.isFetching} onClick={retry}><RefreshCw data-icon="inline-start" />{t('button.refresh', { ns: 'common' })}</Button>
      </div>
    </div>
    <p role="status" className="text-sm text-muted-foreground">{t('handheldGrading.sectionProgress', { done: items.filter(item => item.gradedIsCorrect != null).length, total: items.length })}{data.score != null ? ` · ${t('unitAssessment.score', { score: data.score })}` : ''}</p>
    {error || detail.isError ? <Alert variant="destructive"><AlertDescription>{error ?? t('unitAssessment.loadError')}<Button variant="outline" className="min-h-11" disabled={saving || detail.isFetching} onClick={retry}>{t('roster.retry')}</Button></AlertDescription></Alert> : null}
    {data.status === 'PENDING' ? data.mode === 'PRINT' ? <>
      <Alert><AlertDescription>{t('unitAssessment.startPrintHint')}</AlertDescription></Alert>
      <Button className="min-h-11" disabled={blocked || dirty} onClick={() => void startPrint()}>{t(saving ? 'unitAssessment.starting' : 'unitAssessment.startPrint')}</Button>
    </> : <Alert><AlertDescription><p>{t('unitAssessment.onlinePendingTitle')}</p><p>{t('unitAssessment.onlinePendingHint')}</p></AlertDescription></Alert> : items.length === 0 ? <p>{t('unitAssessment.empty')}</p> : <ol className="flex flex-col gap-4">{items.map((item, index) => {
      const baseline = state.acknowledged[item.quizId] ?? (item.gradedIsCorrect == null ? null : item.gradedIsCorrect ? 1 : 0)
      const value = state.drafts[item.quizId] ?? baseline
      return <li key={item.quizId} className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-4" aria-label={t('gradingMode.rowLabel', { no: index + 1 })}>
        <div className="flex items-center gap-2"><span>{t('gradingMode.rowLabel', { no: index + 1 })}</span>{item.quizId in state.drafts ? <Badge>{t('handheldGrading.unsavedRow')}</Badge> : null}</div>
        <div className="min-w-0 overflow-x-auto text-2xl font-bold text-destructive"><GradingCorrectAnswer correctAnswer={item.correctAnswer} questionSnapshot={item.questionSnapshot} mobile /></div>
        <PartialCreditGradeToggle allowPartial={false} value={value === 1 ? 1 : value === 0 ? 0 : null} disabled={gradingBlocked} onChange={grade => store.getState().setGrade(scope, item.quizId, baseline, grade)} />
        {state.failed[item.quizId] ? <p role="alert" className="text-sm text-destructive">{state.failed[item.quizId]}</p> : null}
      </li>
    })}</ol>}
    {unavailableDrafts.length > 0 ? <Alert variant="destructive"><AlertDescription>{t('handheldGrading.staleTarget')}<Button variant="outline" className="min-h-11" disabled={saving} onClick={() => unavailableDrafts.forEach(id => store.getState().setGrade(scope, id, null, null))}>{t('handheldGrading.discardUnavailable')}</Button></AlertDescription></Alert> : null}
    {data.status !== 'PENDING' && data.status !== 'COMPLETED' && data.status !== 'CANCELLED' ? <Button className="min-h-11" disabled={blocked || dirty || !canComplete || !allGraded} onClick={() => setConfirm(true)}>{completeLabel}</Button> : null}
    {pending > 0 ? <div className={cn('bottom-0 z-20 border-t bg-background p-4 pb-[max(1rem,env(safe-area-inset-bottom))]', workspace ? 'sticky min-w-0' : 'fixed inset-x-0')} data-testid={workspace ? 'workspace-assessment-savebar' : undefined} aria-label={t('handheldGrading.saveBar')}><div className="mx-auto flex max-w-3xl items-center justify-between gap-2"><span role="status">{t(saving ? 'handheldGrading.saving' : 'handheldGrading.unsaved', { count: pending })}</span><Button className="min-h-11" disabled={gradingBlocked} onClick={() => void save()}><Save data-icon="inline-start" />{t('handheldGrading.save', { count: pending })}</Button></div></div> : null}
    <Dialog open={confirm} onOpenChange={open => { if (!saving) setConfirm(open) }}><DialogContent showCloseButton={false}><DialogHeader><DialogTitle>{completeLabel}</DialogTitle><DialogDescription>{t(data.status === 'GRADED' ? 'unitAssessment.openReviewConfirm' : 'unitAssessment.completeConfirm')}</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" className="min-h-11" disabled={saving} onClick={() => setConfirm(false)}>{t('sessionControl.cancel')}</Button><Button className="min-h-11" disabled={blocked || dirty || !canComplete || !allGraded} onClick={() => void complete()}>{t('lessonComplete.confirmLabel')}</Button></DialogFooter></DialogContent></Dialog>
  </section>
}
